import React, { createContext, useCallback, useContext, useEffect, useState, useMemo } from 'react';

import { apiClient } from 'Helpers/network';

import useApiRequest from 'Hooks/useApiRequest';
import useQuery from 'Hooks/useQuery';

const getPersistedExpandedRowsCollapsed = (key) => {
    return localStorage.getItem(key) ? JSON.parse(localStorage.getItem(key)) : {};
};

const PositionActivitiesContext = createContext();

export const useProjectActivitiesContext = () => {
    const context = useContext(PositionActivitiesContext);

    if (!context) {
        throw new Error('useProjectActivitiesContext must be used within a PositionActivitiesProvider');
    }

    return context;
};

export const PositionActivitiesProvider = ({
    fetchPositionActivitiesGanttChartDataUrl,
    fetchPositionActivitiesAllocationsUrl,
    fetchPositionActivitiesCostsUrl,
    updatePositionActivitiesAllocationsUrl,
    position,
    expandAllRowsInitially = false,
    children,
}) => {
    const positionsGanttChartExpandedRowsKey = useMemo(() => `position_${position.id}_activities_gantt_chart_expanded_rows`, [position.id]);

    const [expandedRows, setExpandedRows] = useState(getPersistedExpandedRowsCollapsed(positionsGanttChartExpandedRowsKey));
    const [isResourceAllocationMode, setIsResourceAllocationMode] = useState(false);
    const [filters, setFilters] = useState({});

    const ganttChartRequest = useQuery({
        enabled: false,
        queryFn: ({ filters }) =>
            axios.get(fetchPositionActivitiesGanttChartDataUrl, {
                params: {
                    ...filters,
                },
            }),
    });

    const allocationsRequest = useQuery({
        queryFn: () => apiClient.get(fetchPositionActivitiesAllocationsUrl),
    });

    const activitiesCosts = useQuery({
        queryFn: () => apiClient.get(fetchPositionActivitiesCostsUrl),
    });

    const updateAllocationsRequest = useApiRequest({
        url: updatePositionActivitiesAllocationsUrl,
        errorMessage: $t('Error updating Allocations'),
        successMessage: $t('Allocations updated successfully'),
    });

    useEffect(() => {
        localStorage.setItem(positionsGanttChartExpandedRowsKey, JSON.stringify(expandedRows));
    }, [expandedRows, positionsGanttChartExpandedRowsKey]);

    useEffect(() => {
        ganttChartRequest.refetch({ filters });
    }, [filters]);

    const changeFilters = useCallback(
        (newFilters) => {
            setFilters((filters) => ({ ...filters, ...newFilters }));
        },
        [setFilters],
    );

    const toggleRow = useCallback(
        (key) => {
            setExpandedRows((prev) => ({
                ...prev,
                [key]: !prev[key],
            }));
        },
        [setExpandedRows],
    );

    const toggleAllRows = useCallback(
        (value = true) => {
            let state = {};
            ganttChartRequest.data?.rows_keys?.map((key) => {
                state[key] = value;
            });
            setExpandedRows(state);
        },
        [ganttChartRequest.data, setExpandedRows],
    );

    const updateAllocations = useCallback(
        (allocations) => {
            return updateAllocationsRequest.put({ allocations });
        },
        [updatePositionActivitiesAllocationsUrl],
    );

    const getActivityCostById = useCallback(
        (activityId) => {
            return activitiesCosts.data ? activitiesCosts.data[`id_${activityId}`] || 0 : 0;
        },
        [activitiesCosts.data],
    );

    const getAllocatedPercentageByMonthAndActivityId = useCallback(
        (monthKey, activityId) => {
            return allocationsRequest.data ? allocationsRequest.data[monthKey]?.[`id_${activityId}`] : undefined;
        },
        [allocationsRequest.data],
    );

    useEffect(() => {
        if (expandAllRowsInitially && ganttChartRequest.data) {
            toggleAllRows(true);
        }
    }, [expandAllRowsInitially, toggleAllRows, ganttChartRequest.data]);

    const value = {
        expandedRows,
        filters,
        isResourceAllocationMode,
        ganttChart: {
            data: ganttChartRequest.data,
            loading: ganttChartRequest.loading,
        },
        allocations: {
            data: allocationsRequest.data,
            loading: allocationsRequest.loading,
            refetch: allocationsRequest.refetch,
        },
        activitiesCosts: {
            data: activitiesCosts.data,
            loading: activitiesCosts.loading,
            refetch: activitiesCosts.refetch,
        },
        updateAllocationsRequest,
        getActivityCostById,
        updateAllocations,
        setIsResourceAllocationMode,
        toggleRow,
        toggleAllRows,
        changeFilters,
        getAllocatedPercentageByMonthAndActivityId,
    };

    return <PositionActivitiesContext.Provider value={value}>{children}</PositionActivitiesContext.Provider>;
};

export default PositionActivitiesContext;
